<template>
  <div class="mb-6">
    <div class="flex justify-between items-center mb-4">
      <h3 class="text-lg font-semibold">Шаги операции</h3>
      <button
        @click="$emit('add-step')"
        class="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
      >
        Добавить шаг
      </button>
    </div>

    <div v-if="!steps || steps.length === 0" class="text-gray-500 italic">
      Шаги не добавлены
    </div>

    <div v-else>
      <transition-group
        name="step-list"
        tag="div"
      >
        <div
          v-for="(step, index) in steps"
          :key="step.id || `temp-${index}`"
          :id="step.id ? `step-${step.id}` : null"
          class="mb-6 border rounded-md shadow-sm overflow-hidden"
        >
          <div class="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
            <div class="flex items-center">
              <span class="mr-2 font-semibold">{{ index + 1 }}.</span>
              <span class="font-medium">{{ step.name }}<span class="font-normal text-gray-400 text-xs">{{ stepTypeSuffix(step.type) }}</span></span>
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click="$emit('move-step-up', index)"
                :disabled="index === 0"
                :class="{'opacity-50 cursor-not-allowed': index === 0}"
                class="text-blue-600 hover:text-blue-800 p-1"
              >
                ↑
              </button>
              <button
                @click="$emit('move-step-down', index)"
                :disabled="index === steps.length - 1"
                :class="{'opacity-50 cursor-not-allowed': index === steps.length - 1}"
                class="text-blue-600 hover:text-blue-800 p-1"
              >
                ↓
              </button>
              <button
                @click.stop="$emit('edit-step', step, index)"
                class="p-1 text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-600 rounded transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                v-if="step.id"
                @click.stop="$emit('copy-step', step)"
                title="Копировать шаг"
                class="p-1 text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-600 rounded transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              <button
                @click="$emit('remove-step', index)"
                class="text-red-600 p-1 hover:text-white hover:bg-red-600 border border-red-600 rounded transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>

          <StepContent
            :step="step"
            :productOptions="productOptions"
            :availableDocuments="availableDocuments"
            :showImportButton="step.id === importTargetStepId"
            @change="$emit('change')"
            @add-product="$emit('add-product', $event)"
            @edit-product="forwardEditProduct"
            @remove-product="handleRemoveProduct"
            @add-document="$emit('add-document', $event)"
            @upload-image="$emit('upload-image', $event)"
            @remove-document="$emit('remove-document', $event)"
            @remove-image="$emit('remove-image', $event)"
            @import-excel="$emit('import-excel')"
          />
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import StepContent from './StepContent.vue';

export default {
  components: { StepContent },
  props: {
    steps: { type: Array, default: () => [] },
    productOptions: { type: Array, default: () => [] },
    availableDocuments: { type: Array, default: () => [] },
    importTargetStepId: { type: [Number, String], default: null }
  },
  methods: {
    stepTypeSuffix(type) {
      const map = {
        print: ' (печать)',
        packaging: ' (упаков.)',
        picking: ' (компл.)',
        software: ' (Используемое ПО)',
      };
      return map[type] || '';
    },
    forwardEditProduct(payload) {
      if (!payload?.stepId || !Number.isInteger(payload.productIndex)) {
        console.error('Invalid edit payload:', payload);
        return;
      }

      const step = this.steps.find(s => s.id === payload.stepId);
      if (!step) {
        console.error('Step not found:', payload.stepId);
        return;
      }

      this.$emit('edit-product', {
        step,  // Передаём весь шаг
        index: payload.productIndex,
        productData: payload.product // Добавляем данные продукта
      });
    },
    handleRemoveProduct(payload) {
      console.log('Forwarding product removal:', payload);
      this.$emit('remove-product', {
        step: payload.step,
        index: payload.index
      });
    }
  }
};
</script>

<style scoped>
.step-list-move {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.step-list-enter-active,
.step-list-leave-active {
  transition: all 0.2s ease;
}

.step-list-enter-from,
.step-list-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
